import React, { useEffect, memo } from 'react';
import './MobileNav.css';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  activePage: string;
  onNavigate: (page: string) => void;
  darkMode: boolean;
}

const MobileNav: React.FC<MobileNavProps> = memo(({ 
  isOpen, 
  onClose, 
  activePage, 
  onNavigate,
  darkMode 
}) => {
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavClick = (page: string) => {
    onNavigate(page);
    onClose();
  };

  const navItems = [
    { id: 'landing', label: 'Home', icon: '🏠' },
    { id: 'jobs', label: 'Job Search', icon: '💼' },
    { id: 'companies', label: 'Companies', icon: '🏢' },
    { id: 'salaries', label: 'Salary Explorer', icon: '💰' },
  ];

  return (
    <>
      {/* Overlay */}
      <div 
        className={`mobile-nav-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      
      {/* Navigation Drawer */}
      <nav className={`mobile-nav ${isOpen ? 'open' : ''} ${darkMode ? 'dark' : ''}`}>
        <div className="mobile-nav-header">
          <div className="mobile-nav-logo">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Jobometry</span>
          </div>
          <button 
            className="mobile-nav-close"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        
        <div className="mobile-nav-content">
          <div className="mobile-nav-section">
            <h3>Navigation</h3>
            <ul className="mobile-nav-list">
              {navItems.map(item => (
                <li key={item.id}>
                  <button
                    className={`mobile-nav-item ${activePage === item.id ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.id)}
                  >
                    <span className="mobile-nav-icon">{item.icon}</span>
                    <span className="mobile-nav-label">{item.label}</span>
                    {activePage === item.id && (
                      <svg className="mobile-nav-active" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M7 10l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="mobile-nav-section">
            <h3>Account</h3>
            <div className="mobile-nav-auth">
              <button className="mobile-auth-btn login">Sign In</button>
              <button className="mobile-auth-btn signup">Sign Up</button>
            </div>
          </div>
          
          <div className="mobile-nav-section">
            <h3>Quick Stats</h3>
            <div className="mobile-nav-stats">
              <div className="stat-item">
                <span className="stat-value">15,234</span>
                <span className="stat-label">Active Jobs</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">2,847</span>
                <span className="stat-label">Companies</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">87</span>
                <span className="stat-label">Countries</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mobile-nav-footer">
          <p>© 2024 Jobometry. All rights reserved.</p>
        </div>
      </nav>
    </>
  );
});

MobileNav.displayName = 'MobileNav';

export default MobileNav;